import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SwipeableDrawer } from '@mui/material';

import FollowOutline from './FollowOutline';
import useFollowedEntities, { followedSpeakersFirst } from './useFollowedEntities';
import SpeakerRow from './SpeakerRow';
import EntityInfoDrawer from './EntityInfoDrawer';
import { isUnidentifiedSpeaker } from './speakerHelper';

export const trackSpeakerPresentation = (count, width) => {
    // Framed portraits are 63px with 2px gaps; the badge adds 42px plus an 8px gap.
    const threeFit = width >= (count > 3 ? 243 : 193);
    const visibleCount = Math.min(count, threeFit ? 3 : 2);
    return { visibleCount, wrapBadge: count > visibleCount && width < 178 };
};

const SpeakerPortrait = ({ speaker, followed }) => {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [speaker.imageURL]);
    return (
        <FollowOutline followed={followed} circular aria-hidden='true'>
        <span className='trackInfoCollapsedSpeakerImage'>
            {speaker.imageURL && !failed && (
                <img src={speaker.imageURL} alt='' loading='lazy' onError={() => setFailed(true)} />
            )}
        </span>
        </FollowOutline>
    );
};

const SpeakerRows = ({
  speakers,
  collapseMultiple,
  profileBackLabel,
  // Optional custom class names for styling.
  customContainerClassName,
  customRowClassName,
  rowGap = '12px',
  interactive = true,
  prioritizeFollowed = false,
  preferredSpeakerId,
}) => {
    const isFollowed = useFollowedEntities();
    const [showAllSpeakers, setShowAllSpeakers] = useState(false);
    const [drawerSpeakers, setDrawerSpeakers] = useState([]);
    const [profileSpeakerId, setProfileSpeakerId] = useState(null);
    const [showSpeakerProfile, setShowSpeakerProfile] = useState(false);
    const [availableWidth, setAvailableWidth] = useState(Infinity);
    const containerRef = useRef(null);
    const validSpeakers = Array.isArray(speakers)
        ? speakers.filter(speaker => speaker && !isUnidentifiedSpeaker(speaker)) : [];

    useEffect(() => {
        if (!interactive) {
            setShowAllSpeakers(false);
            setShowSpeakerProfile(false);
        }
    }, [interactive]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!collapseMultiple || !container) return undefined;
        const update = () => {
            const width = container.getBoundingClientRect().width;
            if (width > 0) setAvailableWidth(width);
        };
        update();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
        observer?.observe(container);
        window.addEventListener('resize', update);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [collapseMultiple, validSpeakers.length]);

    if (validSpeakers.length === 0) {
        return null;
    }

    const containerClassName = [
        collapseMultiple ? 'trackInfoSpeakerRows' : '',
        customContainerClassName || '',
    ].filter(Boolean).join(' ');
    const containerStyle = collapseMultiple ? undefined : {
        display: 'flex',
        flexDirection: 'column',
        gap: rowGap,
    };
    if (!collapseMultiple) {
        return (
            <div className={containerClassName} style={containerStyle}>
                {validSpeakers.map((speaker) => (
                    <div key={speaker.id} className={customRowClassName ? customRowClassName : ''}>
                        <SpeakerRow
                            speaker={speaker}
                            showSubscribe={interactive}
                            profileBackLabel={profileBackLabel}
                        />
                    </div>
                ))}
            </div>
        );
    }

    // Keep the drawer and its source portraits in their opening order while
    // preferences update. The drawer retains its snapshot while closing too.
    const currentSpeakers = prioritizeFollowed
        ? followedSpeakersFirst(validSpeakers, speaker => isFollowed('speaker', speaker), preferredSpeakerId) : validSpeakers;
    const orderedSpeakers = showAllSpeakers ? drawerSpeakers : currentSpeakers;
    const openSpeakersDrawer = () => {
        setDrawerSpeakers(orderedSpeakers);
        setShowAllSpeakers(true);
    };
    const { visibleCount, wrapBadge } = trackSpeakerPresentation(orderedSpeakers.length, availableWidth);
    const visibleSpeakers = orderedSpeakers.slice(0, visibleCount);
    const hiddenSpeakerCount = orderedSpeakers.length - visibleSpeakers.length;
    const followedNames = visibleSpeakers.filter(speaker => isFollowed('speaker', speaker))
        .map(speaker => speaker.name || 'speaker');
    const portraitsLabel = orderedSpeakers.length === 1
        ? `Open ${orderedSpeakers[0].name || 'speaker'} profile${followedNames.length ? ', followed' : ''}`
        : `Show all ${orderedSpeakers.length} speakers${followedNames.length ? `; followed: ${followedNames.join(', ')}` : ''}`;

    return (
        <>
            <div className={containerClassName} ref={containerRef} aria-hidden={!interactive || undefined}>
                <button
                    type='button'
                    className={`trackInfoCollapsedSpeakers${wrapBadge ? ' trackInfoCollapsedSpeakersWrapBadge' : ''}`}
                    aria-label={portraitsLabel}
                    disabled={!interactive}
                    onClick={() => {
                        if (validSpeakers.length === 1) {
                            setProfileSpeakerId(validSpeakers[0].id);
                            setShowSpeakerProfile(true);
                        } else {
                            openSpeakersDrawer();
                        }
                    }}
                >
                    <span className='trackInfoSpeakerFaces'>
                        {visibleSpeakers.map((speaker) => <SpeakerPortrait key={speaker.id} speaker={speaker} followed={isFollowed('speaker', speaker)} />)}
                    </span>
                    {hiddenSpeakerCount > 0 && (
                        <span className='trackInfoCollapsedSpeakerMore'>
                            +{hiddenSpeakerCount}
                        </span>
                    )}
                </button>
            </div>

            {profileSpeakerId && (
                <EntityInfoDrawer open={interactive && showSpeakerProfile} onClose={() => setShowSpeakerProfile(false)}
                    speakerId={profileSpeakerId} backLabel={profileBackLabel} />
            )}

            <SwipeableDrawer
                anchor='bottom'
                open={interactive && showAllSpeakers}
                onClose={() => setShowAllSpeakers(false)}
                onOpen={openSpeakersDrawer}
                disableDiscovery
                ModalProps={{ keepMounted: false }}
                PaperProps={{
                    sx: {
                        borderRadius: '18px 18px 0px 0px',
                    },
                }}
            >
                <div className='trackInfoSpeakersDrawer appDrawerSurface'>
                    <div className='previewPreferenceDragIndicator' />
                    <div className='trackInfoSpeakersDrawerTitle'>
                        {drawerSpeakers.length === 1 ? 'Speaker' : 'Speakers'}
                    </div>
                    <div className='trackInfoSpeakersDrawerRows'>
                        {drawerSpeakers.map((speaker) => (
                            <div key={speaker.id} className={customRowClassName ? customRowClassName : ''}>
                                <SpeakerRow
                                    speaker={speaker}
                                    showSubscribe={interactive}
                                    profileBackLabel={profileBackLabel}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </SwipeableDrawer>
        </>
    );
};

export default SpeakerRows;
